import { useDispatch, useSelector } from 'react-redux';
import { useForm } from '@mantine/form';
import { useState } from 'react';
import {
  TextInput,
  Button,
  Grid,
  Group,
  Text,
  NumberInput,
  Stack,
} from '@mantine/core';
import Modal from '@/components/Modal';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import {
  IconPhone,
  IconUser,
  IconHome,
  IconBuildingBank,
  IconMessageCircleQuestion,
} from '@tabler/icons-react';

import { Page, ProcessHeader } from '@/components';
import { getRouteById, routeWithParams } from '@/helpers/routes';
import { updateExtraDetails } from '@/data/features/proposal/proposal';
import validate from '@/helpers/validations';
import routes from '@/routes';
import { useBusinessCreditContactMutation, useSendingToTheCustomerForSignatureMutation } from '@/services/api';
import { getMutationResult } from '@/helpers/query';
import { useIdInStoreGuard } from '@/hooks/useIdfromUrl';

const BusinessCredit = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [apiBusinessCreditContact] = useBusinessCreditContactMutation();
  const [apiConfigSendToClientForSignature] = useSendingToTheCustomerForSignatureMutation();

  const borrower = useSelector((state) => state.proposal.borrower?.[0] || {});
  const proposal = useSelector((state) => state.proposal);
  useIdInStoreGuard();

  // Validation messages
  const required = t('globals.validations.required');
  const hebrew = t('globals.validations.hebrew');
  const numbers = t('globals.validations.number');
  const phone = t('globals.validations.phone');

  const formValidations = {
    companyName: (value) => validate(value, { required, hebrew }),
    companyNumber: (value) => validate(value, { required, numbers }),
    creditAmount: (value) => validate(value, { required }),
    contactName: (value) => validate(value, { required, hebrew }),
    contactPhone: (value) => validate(value, { required, numbers, phone }),
    contactRole: (value) => validate(value, { required, hebrew }),
  };

  const form = useForm({
    mode: 'controlled',
    validateInputOnBlur: true,
    initialValues: {
      companyName: '',
      companyNumber: '',
      creditAmount: '',
      contactName: borrower.fullName || '',
      contactPhone: borrower.phoneNumber || '',
      contactRole: '',
    },
    validate: formValidations,
  });

  const handleContinue = async () => {
    const validation = form.validate();

    if (validation.hasErrors) {
      notifications.show({
        title: t('globals.error.title'),
        message: t('pages.businessCredit.error.fillRequiredFields'),
        color: 'red',
      });
      return;
    }

    try {
      setLoading(true);

      // Save business credit data to store
      dispatch(updateExtraDetails({
        businessCredit: form.values,
      }));

      // Prepare the data to send via API
      const sendValues = {
        companyName: form.values.companyName,
        companyNumber: form.values.companyNumber,
        creditAmount: form.values.creditAmount,
        contactName: form.values.contactName,
        contactPhone: form.values.contactPhone,
        contactRole: form.values.contactRole,
        offerId: proposal.id,
      };

      // Send data via API
      const {
        data,
        isError,
      } = await getMutationResult(sendValues, apiBusinessCreditContact);

      const sendSummeryLinkObj = {
        phoneNumber: borrower.phoneNumber,
        fullName: borrower.fullName,
        email: borrower.email,
        proposalNumber: id,
      };

      await getMutationResult(sendSummeryLinkObj, apiConfigSendToClientForSignature);

      if (isError || !data) {
        notifications.show({
          title: t('globals.error.title'),
          message: t('pages.businessCredit.error.saveError'),
          color: 'red',
        });
        return;
      }
      // Navigate to summary page after successful API call
      const successPage = routes.newProposal.pages.find((page) => page.id === 'success');
      navigate(routeWithParams(successPage.path, { id }));
    } catch (error) {
      notifications.show({
        title: t('globals.error.title'),
        message: t('pages.businessCredit.error.saveError'),
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReturn = () => {
    navigate(routeWithParams(getRouteById(routes.newProposal.id, 'reason')?.path, { id }));
  };

  const handleReturnToMain = () => {
    setShowModal(false);
    dispatch({ type: 'RESET_ALL' });
    navigate('/');
  };

  return (
    <div>
      <form onSubmit={form.onSubmit(handleContinue)}>
        <ProcessHeader
          headerText={t('pages.businessCredit.title')}
          headerColor="#E0EDFF"
          headerTextColor="#1C2EB0"
          subtitle={t('pages.businessCredit.subtitle')}
          showReturnButton
          onReturn={handleReturn}
          icon={IconBuildingBank}
        >

          {/* Company Information Section */}
          <Page.Section>
            <Text size="lg" mb="md" fw={600}>
              {t('pages.businessCredit.companyInfo.title')}
            </Text>

            <Grid>
              <Grid.Col span={6}>
                <TextInput
                  label={t('pages.businessCredit.companyInfo.companyName')}
                  placeholder={t('pages.businessCredit.companyInfo.companyNamePlaceholder')}
                  required
                  {...form.getInputProps('companyName')}
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <TextInput
                  label={t('pages.businessCredit.companyInfo.companyNumber')}
                  placeholder={t('pages.businessCredit.companyInfo.companyNumberPlaceholder')}
                  required
                  {...form.getInputProps('companyNumber')}
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <NumberInput
                  label={t('pages.businessCredit.companyInfo.creditAmount')}
                  placeholder={t('pages.businessCredit.companyInfo.creditAmountPlaceholder')}
                  required
                  min={0}
                  thousandSeparator=","
                  suffix=" ₪"
                  {...form.getInputProps('creditAmount')}
                />
              </Grid.Col>
            </Grid>
          </Page.Section>

          {/* Contact Information Section */}
          <Page.Section>
            <Text mb='md' size="lg" fw={600}>
              {t('pages.businessCredit.contactInfo.title')}
            </Text>

            <Grid>
              <Grid.Col span={6}>
                <TextInput
                  label={t('pages.businessCredit.contactInfo.contactName')}
                  placeholder={t('pages.businessCredit.contactInfo.contactNamePlaceholder')}
                  required
                  leftSection={<IconUser size={16} />}
                  {...form.getInputProps('contactName')}
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <TextInput
                  label={t('pages.businessCredit.contactInfo.contactPhone')}
                  placeholder={t('pages.businessCredit.contactInfo.contactPhonePlaceholder')}
                  required
                  leftSection={<IconPhone size={16} />}
                  {...form.getInputProps('contactPhone')}
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <TextInput
                  label={t('pages.businessCredit.contactInfo.contactRole')}
                  placeholder={t('pages.businessCredit.contactInfo.contactRolePlaceholder')}
                  required
                  {...form.getInputProps('contactRole')}
                />
              </Grid.Col>
            </Grid>
          </Page.Section>

          <Page.Actions>
            <Group>
              <Button
                variant="outline"
                color="blue"
                onClick={handleContinue}
                loading={loading}
                rightSection={<IconMessageCircleQuestion size={16} />}
              >
                {t('pages.businessCredit.transferToConsultant')}
              </Button>
            </Group>
          </Page.Actions>
        </ProcessHeader>
      </form>

      {/* Modal for after form submission */}
      <Modal
        opened={showModal}
        showClose={false}
        onClose={() => setShowModal(false)}
        title={t('pages.businessCredit.title')}
        centered
        size="md"
      >
        <Stack gap="lg" p="md">
          <Text ta="center" size="lg" c="green" fw={600}>
            {t('pages.businessCredit.modal.successMessage')}
          </Text>

          <Group justify="center" gap="md">
            <Button
              variant="filled"
              color="green"
              onClick={handleReturnToMain}
              leftSection={<IconHome size={16} />}
            >
              {t('pages.businessCredit.modal.returnToMain')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
};

export default BusinessCredit;
